import React, { useCallback } from 'react';
import { action } from 'mobx';
import { observer } from 'mobx-react';

import { Modal } from 'ui/base/modal';
import { BooleanStore } from 'ui/base/input';
import { ButtonClickEvent } from 'ui/base/types';
import { Theme, ThemedStyles, useStyles } from 'ui/base/theme';
import { BackButton } from 'ui/base/button/back_button';
import { StandardPage } from 'ui/base/page/standard_page';

import { LinkButton } from 'ui/public/button';
import { CheckboxInput } from 'ui/standard/input/checkbox_input';

import {
  Seen,
  getFullSeen,
  getShortSeen,
  getMicroSeen,
} from 'assets/enums/seen/seen';

import { SeenStore } from './seen_store';
import baseStyles from './seen_input.module.css';
import SeenInputMessages from './seen_input_messages';

// Style declarations and themes
type SeenInputStyles = {
  box: string;
  placeholder: string;
  modalContainer: string;
  header: string;
  commit: string;
  toggleAll: string;
  inputRow: string;
  inputLabel: string;
  inputLabelSymbol: string;
  inputLabelText: string;
};

const themedStyles: ThemedStyles<SeenInputStyles> = {
  [Theme.DAYLIGHT]: {
    box: baseStyles.daylightBox,
    placeholder: baseStyles.daylightPlaceholder,
    modalContainer: baseStyles.modalContainer,
    header: baseStyles.header,
    commit: baseStyles.daylightCommit,
    toggleAll: baseStyles.daylightToggleAll,
    inputRow: baseStyles.inputRow,
    inputLabel: baseStyles.inputLabel,
    inputLabelSymbol: baseStyles.inputLabelSymbol,
    inputLabelText: baseStyles.inputLabelText,
  },
  [Theme.MIDNIGHT]: {
    box: baseStyles.midnightBox,
    placeholder: baseStyles.midnightPlaceholder,
    modalContainer: baseStyles.modalContainer,
    header: baseStyles.header,
    commit: baseStyles.midnightCommit,
    toggleAll: baseStyles.midnightToggleAll,
    inputRow: baseStyles.inputRow,
    inputLabel: baseStyles.inputLabel,
    inputLabelSymbol: baseStyles.inputLabelSymbol,
    inputLabelText: baseStyles.inputLabelText,
  },
};

// A component to render a row in the modal
type SeenInputRowProps = {
  symbol: string;
  text: string;
  store: BooleanStore;
  styles: SeenInputStyles;
};

const SeenInputRow = observer(({ symbol, text, store, styles }: SeenInputRowProps) => {
  const toggleStore = useCallback(action(() => {
    store.value = !store.value;
  }), [store]);

  return (
    <div className={styles.inputRow}>
      <div className={styles.inputLabel} onClick={toggleStore}>
        <div className={styles.inputLabelSymbol}>
          {symbol}
        </div>
        <div className={styles.inputLabelText}>
          {text}
        </div>
      </div>
      <CheckboxInput store={store}/>
    </div>
  );
});

// The component that handles the actual modal
type SeenInputModalProps = {
  store: SeenStore;
};

const SeenInputModal = observer(({ store }: SeenInputModalProps) => {
  const styles = useStyles(themedStyles);

  const onCommit = React.useCallback(action((event: ButtonClickEvent) => {
    event.stopPropagation();
    store.commitWorkspace();
    store.modal.hideModal();
  }), [store]);

  // Note: toggleAll functionality can be abstracted away into a component that
  // takes a list of boolean stores as argument. We can do that once a uniform
  // 'styling' for this feature has been decided.
  const onToggleAll = React.useCallback(action((event: ButtonClickEvent) => {
    event.stopPropagation();
    const toCheckAll = !store.workspaceHasChecked;
    Object.values(store.workspace).forEach((booleanStore) => {
      booleanStore.value = toCheckAll;
    });
  }), [store]);

  return (
    <div className={styles.modalContainer}>
      <StandardPage>
        <div className={styles.header}>
          <BackButton onClick={store.modal.hideModal}/>
          <button type='button' className={styles.commit} onClick={onCommit}>
            {SeenInputMessages.ok()}
          </button>
        </div>
        <div className={styles.inputRow}>
          <LinkButton type='button' className={styles.toggleAll} onClick={onToggleAll}>
            {!store.workspaceHasChecked ? SeenInputMessages.checkAll() : SeenInputMessages.uncheckAll()}
          </LinkButton>
        </div>
        <SeenInputRow
          styles={styles}
          symbol={getMicroSeen(Seen.SEEN)}
          text={getShortSeen(Seen.SEEN)}
          store={store.workspace[Seen.SEEN]}
        />
        <SeenInputRow
          styles={styles}
          symbol={getMicroSeen(Seen.UNSEEN)}
          text={getShortSeen(Seen.UNSEEN)}
          store={store.workspace[Seen.UNSEEN]}
        />
      </StandardPage>
    </div>
  );
});

// The component that handles the actual modal
type SeenInputBoxProps = {
  placeholder: string;
  store: SeenStore;
};

const SeenInputBox = observer(({ placeholder, store }: SeenInputBoxProps) => {
  const styles = useStyles(themedStyles);
  const onShowModal = React.useCallback(action(() => {
    store.reloadWorkspace();
    store.modal.showModal();
  }), [store]);

  if (store.arrayValue.length > 0) {
    return (
      <button type='button' className={styles.box} onClick={onShowModal}>
        {store.arrayValue.map((act) => getFullSeen(act)).join(', ')}
      </button>
    );
  } else {
    return (
      <button type='button' className={styles.box} onClick={onShowModal}>
        <span className={styles.placeholder}>{placeholder}</span>
      </button>
    );
  }
});

export type SeenInputProps = {
  placeholder: string;
  store: SeenStore;
};

export const SeenInput = observer(({ placeholder, store }: SeenInputProps) => {
  return (
    <>
      <Modal store={store.modal}>
        <SeenInputModal store={store}/>
      </Modal>
      <SeenInputBox placeholder={placeholder} store={store}/>
    </>
  );
});
